<template>
  <b-modal
    v-model="showModal"
    size="lg"
    id="manage-return-modal"
    :title="`View / Edit return #${edited.id}`"
    :ok-title="isFormLoading ? 'Saving...' : 'Save'"
    :ok-disabled="isFormLoading || isContentLoading"
    cancel-variant="outline-secondary"
    @ok="onSubmit($event)"
  >
    <div
      v-if="isContentLoading"
      class="text-center pt-3"
    >
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group label="Reason" label-for="edit-return-reason">
            <b-form-input
              id="edit-return-reason"
              v-model="edited.reason"
              placeholder="Reason for return"
            />
            <small v-if="v$.edited?.reason.$error" class="text-danger">Reason is required.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Return date" label-for="edit-return-date">
            <b-form-datepicker
              id="edit-return-date"
              v-model="edited.return_date"
              reset-button
            />
            <small v-if="v$.edited?.return_date.$error" class="text-danger">Return date is required.</small>
          </b-form-group>
        </b-col>
      </b-row>

      <h6 class="mt-1 mb-1">Units to return</h6>
      <div
        v-for="unit in unitRows"
        :key="unit.unit_id"
        class="d-flex align-items-center mb-1 border rounded p-1"
      >
        <b-form-checkbox
          v-model="unit.selected"
          class="mr-1"
          :disabled="unit.max_quantity <= 0"
          @change="onUnitSelectionChange(unit)"
        />
        <div class="flex-grow-1 mr-1">
          <div class="font-weight-bold">{{ unit.name }}</div>
          <small class="text-muted">Remaining returnable: {{ unit.max_quantity }}</small>
        </div>
        <b-form-input
          type="number"
          min="1"
          :max="unit.max_quantity"
          step="1"
          class="unit-qty-input"
          v-model.number="unit.quantity"
          :disabled="!unit.selected || unit.max_quantity <= 0"
          style="width: 90px;"
        />
      </div>
      <small v-if="unitsError" class="text-danger d-block mb-1">{{ unitsError }}</small>

      <b-row class="mt-1">
        <b-col cols="12">
          <b-form-group label="Refund amount" label-for="edit-return-refund-amount">
            <b-form-input
              id="edit-return-refund-amount"
              v-model.number="edited.refund_amount"
              type="number"
              min="0"
              :max="maxRefundable"
              step="0.01"
            />
            <small class="text-muted d-block mt-25">
              Max refund: {{ $formatCurrency(maxRefundable, null) }}
            </small>
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-group label="Refund method" label-for="edit-return-refund-method">
            <b-form-select
              id="edit-return-refund-method"
              v-model="edited.refund_method"
              :options="paymentMethods"
              :disabled="!edited.refund_amount || edited.refund_amount <= 0"
            />
          </b-form-group>
        </b-col>
      </b-row>

      <b-row>
        <b-col cols="12">
          <b-form-checkbox
            v-model="edited.restore_to_stock"
            switch
            class="mb-1"
          >
            Put the returned products back to stock
          </b-form-checkbox>
        </b-col>
      </b-row>
    </b-form>
  </b-modal>
</template>

<script setup>
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'
import {
  BCol,
  BForm,
  BFormCheckbox,
  BFormDatepicker,
  BFormGroup,
  BFormInput,
  BFormSelect,
  BModal,
  BRow,
  BSpinner,
} from 'bootstrap-vue'
import { PAYMENT_METHODS, DEFAULT_PAYMENT_METHOD } from '@/constants/enums'
import { toastFailure, toastSuccess } from '@/libs/toastification'
import { backendErrorString } from '@/libs/utils'
import { companyTodayYmd } from '@/libs/companyTime'

const props = defineProps({
  sale: {
    type: Object,
    default: null,
  },
  returnId: {
    type: Number,
    default: null,
  },
  clickDate: {
    type: Date,
    required: true,
  },
})

const emit = defineEmits(['saved'])

const store = useStore()
const showModal = ref(false)
const isContentLoading = ref(true)
const isFormLoading = ref(false)
const unitsError = ref('')
const unitRows = ref([])
const returnContext = ref(null)
const paymentMethods = PAYMENT_METHODS

const edited = reactive({
  id: null,
  reason: '',
  return_date: null,
  refund_amount: 0,
  refund_method: DEFAULT_PAYMENT_METHOD,
  restore_to_stock: false,
})

const rules = {
  edited: {
    reason: { required },
    return_date: { required },
  },
}
const v$ = useVuelidate(rules, { edited })

const maxRefundable = computed(() => Number(returnContext.value?.max_refundable ?? 0))

const todayDateString = () => companyTodayYmd()

const buildUnitRows = (context, productReturn = null) => {
  const returnUnits = {}
  if (productReturn && Array.isArray(productReturn.units)) {
    productReturn.units.forEach((unit) => {
      returnUnits[unit.id] = unit.quantity
    })
  }

  unitRows.value = (context?.units || []).map((unit) => {
    const maxQuantity = Number(unit.remaining_returnable_quantity ?? 0)
    const isSelected = Object.prototype.hasOwnProperty.call(returnUnits, unit.id)

    return {
      unit_id: unit.id,
      name: unit.name,
      max_quantity: maxQuantity,
      selected: isSelected && maxQuantity > 0,
      quantity: returnUnits[unit.id] || (maxQuantity > 0 ? maxQuantity : 1),
    }
  })
}

const loadModalData = async () => {
  if (!props.sale?.id || !props.returnId) {
    return
  }

  isContentLoading.value = true
  unitsError.value = ''

  try {
    returnContext.value = await store.dispatch('returns/loadReturnContext', {
      saleId: props.sale.id,
      excludeReturnId: props.returnId,
    })
    const productReturn = await store.dispatch('returns/loadReturn', props.returnId)
    edited.id = productReturn.id
    edited.reason = productReturn.reason || ''
    edited.return_date = productReturn.return_date || todayDateString()
    edited.refund_amount = Number(productReturn.refund_amount || 0)
    edited.refund_method = productReturn.refund_method || DEFAULT_PAYMENT_METHOD
    edited.restore_to_stock = !!productReturn.restore_to_stock
    buildUnitRows(returnContext.value, productReturn)
    showModal.value = true
  } catch (error) {
    showModal.value = false
    toastFailure(backendErrorString(error))
  } finally {
    isContentLoading.value = false
  }
}

watch(
  () => props.clickDate,
  () => {
    if (!props.sale || !props.returnId) {
      return
    }
    loadModalData()
  },
)

const onUnitSelectionChange = (unit) => {
  if (unit.selected && (!unit.quantity || unit.quantity < 1)) {
    unit.quantity = unit.max_quantity > 0 ? unit.max_quantity : 1
  }
}

const validateUnits = () => {
  const selectedUnits = unitRows.value.filter((unit) => unit.selected && unit.quantity > 0)
  if (!selectedUnits.length) {
    unitsError.value = 'Select at least one unit with quantity greater than zero.'
    return false
  }

  for (const unit of selectedUnits) {
    if (unit.quantity > unit.max_quantity) {
      unitsError.value = `Quantity for ${unit.name} cannot exceed ${unit.max_quantity}.`
      return false
    }
  }

  unitsError.value = ''
  return true
}

const onSubmit = async (event) => {
  event.preventDefault()
  if (isFormLoading.value) {
    return
  }

  const isFormCorrect = await v$.value.edited.$validate()
  if (!isFormCorrect || !validateUnits()) {
    return
  }

  const refundAmount = Number(edited.refund_amount || 0)
  if (refundAmount > maxRefundable.value) {
    toastFailure(`Refund amount cannot exceed ${maxRefundable.value}.`)
    return
  }

  if (refundAmount > 0 && !edited.refund_method) {
    toastFailure('Refund method is required when refund amount is greater than zero.')
    return
  }

  isFormLoading.value = true

  const payload = {
    reason: edited.reason,
    return_date: edited.return_date,
    refund_amount: refundAmount,
    refund_method: refundAmount > 0 ? edited.refund_method : null,
    restore_to_stock: edited.restore_to_stock,
    units: unitRows.value
      .filter((unit) => unit.selected && unit.quantity > 0)
      .map((unit) => ({
        unit_id: unit.unit_id,
        quantity: unit.quantity,
        selected: true,
      })),
  }

  try {
    await store.dispatch('returns/updateReturn', { id: props.returnId, payload })
    toastSuccess('Return updated successfully.')
    showModal.value = false
    const context = await store.dispatch('returns/loadReturnContext', {
      saleId: props.sale.id,
    })
    emit('saved', {
      has_returns: !!context?.has_returns,
      has_any_returned: !!context?.has_any_returned,
      has_remaining_to_return: !!context?.has_remaining_to_return,
    })
  } catch (error) {
    toastFailure(backendErrorString(error))
  } finally {
    isFormLoading.value = false
  }
}
</script>

<style scoped>
.unit-qty-input {
  flex-shrink: 0;
}
</style>
